import { trackProductEvent } from '../lib/product-analytics';
import { useEffect, useRef } from 'react';
import { AppState, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../lib/query-keys';
import {
  fetchNotificationUnreadCount,
  markNotificationRead,
} from '../lib/notifications-api';
import {
  setNotificationUnreadCount,
  updateNotificationUnreadCount,
} from '../lib/notification-count';
import { resolveNotificationTarget } from '../lib/notification-target';

/**
 * Sets up notification presentation and tap-to-navigate handling.
 * - Shows the OS banner only when the app is not on screen (in-app toast covers
 *   the foreground case, so the two do not double up)
 * - Navigates to deep link URL on tap
 * - Syncs badge count with app icon
 * - No-op on web
 */
export function useNotificationHandler() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const handledResponseIds = useRef(new Set<string>());

  useEffect(() => {
    if (Platform.OS === 'web') return;

    const Notifications = require('expo-notifications');

    // The server no longer withholds push while a mobile socket is open (that
    // silenced backgrounded phones entirely), so presentation is decided here.
    // While the app is actually on screen the socket event already renders an
    // in-app toast: showing an OS banner too would double up. Anything that
    // arrives while the app is backgrounded or inactive gets the full banner,
    // which is the case that was broken.
    Notifications.setNotificationHandler({
      handleNotification: async () => {
        const isForeground = AppState.currentState === 'active';
        return {
          shouldShowBanner: !isForeground,
          shouldShowList: true,
          shouldPlaySound: !isForeground,
          shouldSetBadge: true,
        };
      },
    });

    const handleResponse = (response: any) => {
      const actionIdentifier = response?.actionIdentifier;
      const defaultActionIdentifier = Notifications.DEFAULT_ACTION_IDENTIFIER;
      if (
        defaultActionIdentifier &&
        actionIdentifier &&
        actionIdentifier !== defaultActionIdentifier
      ) {
        return;
      }

      const notification = response?.notification;
      const responseId =
        typeof notification?.request?.identifier === 'string'
          ? notification.request.identifier
          : null;
      if (responseId) {
        if (handledResponseIds.current.has(responseId)) {
          return;
        }
        handledResponseIds.current.add(responseId);
      }

      const data = notification?.request?.content?.data;
      const notificationId =
        data && typeof data.notificationId === 'string' ? data.notificationId : null;
      if (notificationId) {
        updateNotificationUnreadCount(queryClient, (current) => current - 1);
        markNotificationRead(notificationId)
          .then(async () => {
            const count = await fetchNotificationUnreadCount();
            setNotificationUnreadCount(queryClient, count);
          })
          .catch(() => {
            fetchNotificationUnreadCount()
              .then((count) => setNotificationUnreadCount(queryClient, count))
              .catch(() => {
                void queryClient.invalidateQueries({
                  queryKey: QUERY_KEYS.NOTIFICATION_UNREAD_COUNT,
                });
              });
          });
      }

      const target = resolveNotificationTarget(data);
      // Push → session attribution: app_opened cannot tell a tap from a launch.
      trackProductEvent('notification_opened', {
        notification_kind:
          data && typeof data.type === 'string'
            ? data.type
            : data && typeof data.kind === 'string'
              ? data.kind
              : 'unknown',
        has_target: Boolean(target),
      });
      if (target) {
        router.push(target as any);
      }

      if (typeof Notifications.clearLastNotificationResponse === 'function') {
        Notifications.clearLastNotificationResponse();
      }
    };

    const lastResponse =
      typeof Notifications.getLastNotificationResponse === 'function'
        ? Notifications.getLastNotificationResponse()
        : null;
    if (lastResponse) {
      handleResponse(lastResponse);
    }

    // Handle notification tap: navigate to deep link
    const responseSubscription =
      Notifications.addNotificationResponseReceivedListener(
        handleResponse,
      );

    // Handle notification received in foreground (for badge sync)
    const receivedSubscription =
      Notifications.addNotificationReceivedListener(() => {
        // Badge count is set by the push payload (badge field)
        // No additional action needed here
      });

    return () => {
      responseSubscription.remove();
      receivedSubscription.remove();
    };
  }, [queryClient, router]);
}
